import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Layout from '../layout/Layout'
import ClientsFilterBar from '../molecules/ClientsFilterBar'
import ClientTableRow from '../molecules/ClientTableRow'
import DeleteCustomerModal from '../molecules/DeleteCustomerModal'
import TablePagination from '../molecules/TablePagination'
import { Client, DELETE_CLIENT, FETCH_CLIENTS, SET_CURRENT_PAGE } from 'src/redux/clientsReducer'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducer } from 'src/redux/rootReducer'

const Clients = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const clients = useSelector((state: RootReducer) => state.clientsReducer.clients)
  const currentPage = useSelector((state: RootReducer) => state.clientsReducer.currentPage)
  const totalPages = useSelector((state: RootReducer) => state.clientsReducer.totalPages)

  const [showDeleteForm, setShowDeleteForm] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<Client | null>(null)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  useEffect(() => {
    dispatch(FETCH_CLIENTS())
  }, [dispatch, currentPage])

  const handleDeleteCustomer = (): void => {
    if (selectedCustomer !== null) {
      dispatch(DELETE_CLIENT(selectedCustomer.id))
    }
    setShowDeleteForm(false)
    setSelectedCustomer(null)
  }

  const handlePageChange = (page: number): void => {
    setOpenMenuId(null)
    dispatch(SET_CURRENT_PAGE(page))
  }

  return (
    <Layout withoutToolbar>
      <div className="flex flex-col justify-start items-start gap-5 h-[calc(100vh-100px)]">
        <ClientsFilterBar
          onAddClient={() => {
            navigate('/selling/clients/create')
          }}
        />

        <div className="self-stretch flex-1 relative bg-backgrounds-bg-field-component rounded-lg outline outline-1 outline-offset-[-1px] outline-borders-border-base flex flex-col justify-start items-start overflow-hidden">
          <div className="self-stretch px-6 py-3 bg-backgrounds-bg-component inline-flex justify-center items-center gap-3">
            <div className="flex-1 self-stretch flex justify-start items-center gap-3">
              <div className="flex-1 self-stretch flex justify-start items-center overflow-hidden">
                <div className="justify-start text-foregrounds-fg-subtle text-xs font-medium font-Montserrat leading-5">
                  {t('clients.name')}
                </div>
              </div>
              <div className="flex-1 self-stretch flex justify-start items-center overflow-hidden">
                <div className="justify-start text-foregrounds-fg-subtle text-xs font-medium font-Montserrat leading-5">
                  {t('clients.email')}
                </div>
              </div>
              <div className="w-48 self-stretch flex justify-start items-center overflow-hidden">
                <div className="justify-start text-foregrounds-fg-subtle text-xs font-medium font-Montserrat leading-5">
                  {t('clients.phone')}
                </div>
              </div>
              <div className="w-40 self-stretch flex justify-start items-center overflow-hidden">
                <div className="justify-start text-foregrounds-fg-subtle text-xs font-medium font-Montserrat leading-5">
                  {t('clients.country')}
                </div>
              </div>
              <div className="self-stretch flex justify-end items-center overflow-hidden">
                <div className="justify-start text-foregrounds-fg-subtle text-xs font-medium font-Montserrat leading-5">
                  {t('clients.actions')}
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch flex-1 flex flex-col justify-start items-start overflow-y-auto">
            {clients.length === 0 && (
              <div className="self-stretch flex-1 p-6 flex flex-col justify-center items-center gap-1">
                <div className="text-center text-foregrounds-fg-base text-xl font-semibold font-Montserrat leading-5">
                  {t('clients.empty_title')}
                </div>
                <div className="text-center text-foregrounds-fg-subtle text-sm font-medium font-Montserrat leading-5">
                  {t('clients.empty_subtitle')}
                </div>
              </div>
            )}
            {clients.map((customer) => (
              <ClientTableRow
                key={customer.id}
                name={customer.name}
                email={customer.email}
                phoneNumber={customer.phone_number}
                country={customer.country}
                clientType={customer.client_type}
                isMenuOpen={openMenuId === customer.id}
                onToggleMenu={() => {
                  setOpenMenuId(openMenuId === customer.id ? null : customer.id)
                }}
                onCloseMenu={() => {
                  setOpenMenuId(null)
                }}
                onOpenDetails={() => {
                  navigate(`/selling/clients/${customer.id}`)
                }}
                onEdit={() => {
                  navigate(`/selling/clients/${customer.id}/edit`)
                }}
                onDelete={() => {
                  setOpenMenuId(null)
                  setSelectedCustomer(customer)
                  setShowDeleteForm(true)
                }}
              />
            ))}
          </div>

          <TablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>

        <DeleteCustomerModal
          isOpen={showDeleteForm}
          onClose={() => {
            setShowDeleteForm(false)
            setSelectedCustomer(null)
          }}
          onConfirm={handleDeleteCustomer}
          customer={selectedCustomer}
        />
      </div>
    </Layout>
  )
}

export default Clients
